import { isUndefined, noop } from 'lodash';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/Button';
import { DayPicker } from 'components/DayPicker/DayPicker';
import { TimeSlotPicker } from 'components/TimeSlotPicker/TimeSlotPicker';
import { SelectInput } from 'components/Forms';
import { Heading } from 'components/Typography';
import { getFirstAvailableDayIndex } from 'domains/consultations/freeConsultationSchedule';
import { TimeZone, TimezoneOption, timeZoneOptions } from 'domains/timezone';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { useModal } from 'utils/hooks/useModal';
import { CantFindTimeModal } from 'domains/consultations/Components/CantFindTimeModal';
import { AvailableTimes } from 'domains/calendars/types';

type Props = {
  defaultValue?: Date;
  onSubmit: (value: Date, displayTimezone: TimeZone) => void;
  availableTimes: AvailableTimes;
  supportEmail?: string;
};

const ScheduleConsultation: React.FC<Props> = ({ onSubmit, availableTimes, supportEmail }) => {
  const { displayTimezone, setDisplayTimezone } = useDisplayTimezone();
  const { modalProps, openModal } = useModal();

  const [selectedDayIndex, setSelectedDayIndex] = useState<number | undefined>(
    getFirstAvailableDayIndex(availableTimes),
  );
  const [timeSlot, setTimeSlot] = useState<Date | undefined>();

  const currentSlots = isUndefined(selectedDayIndex) ? undefined : availableTimes[selectedDayIndex].availableTimes;

  const onChangeDay = useCallback((newDayIndex: number) => {
    setSelectedDayIndex(newDayIndex);
    setTimeSlot(undefined);
  }, []);

  const { t } = useTranslation();

  const selectInputRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    if (selectInputRef.current) {
      selectInputRef.current.focus();
    }
  }, []);

  return (
    <>
      <Heading level="h2" className="mb-16 text-left">
        {t(`consultation.schedule.title`)}
      </Heading>
      <Heading level="h7" className="mb-64 sm-d:mb-40">
        {t(`consultation.schedule.subtitle`)}
      </Heading>

      <SelectInput
        data-testid="schedule-timezone-select"
        label={t('consultation.schedule.timezone')}
        options={timeZoneOptions}
        name="displayTimezone"
        value={displayTimezone?.value}
        onChange={(e: ChangeEvent<HTMLSelectElement>) => {
          setDisplayTimezone(timeZoneOptions.find(({ value }) => value === e.target.value) as TimezoneOption);
        }}
        clearValue={noop}
        setError={noop}
        setValue={noop}
        disabled={false}
        className="mb-64 sm-d:mb-40"
        allowEmpty
        hint={isUndefined(displayTimezone) ? t('consultation.schedule.enterTimezone') : undefined}
        ref={selectInputRef}
      />
      {displayTimezone && (
        <>
          <DayPicker selectedDayIndex={selectedDayIndex} onChange={onChangeDay} availableTimes={availableTimes} />

          {currentSlots?.length && <TimeSlotPicker onChange={setTimeSlot} timeSlots={currentSlots} value={timeSlot} />}

          <div className="flex sm-d:flex-col gap-16">
            <Button
              data-testid="schedule-submit-button"
              className="sm-d:w-full"
              size="lg"
              disabled={isUndefined(timeSlot)}
              onClick={() => onSubmit(timeSlot as Date, displayTimezone.value)}
            >
              {t('consultation.schedule.submit')}
            </Button>

            <Button className="sm-d:w-full" size="lg" variant="ghost" onClick={openModal}>
              {t('consultation.schedule.noTimes')}
            </Button>
          </div>
        </>
      )}

      {supportEmail && <CantFindTimeModal {...modalProps} supportEmail={supportEmail} />}
    </>
  );
};

export default ScheduleConsultation;
